// Weekly Brief & Governance Dashboard
// 21 Oct 2026 (security follow-up, item 15): /metrics/success is removed (it was
// built entirely from auto-approval decisions and purchases, both retired/paused),
// so the KPI cards and Decision Breakdown tab are gone. The Weekly Brief is now
// view-only (brief generation is paused — PMS is frozen on its last snapshot).
// Governance keeps only the config-change history.
import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Shield, Loader2, FileText, Download
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

export default function MetricsPage() {
  const { user } = useAuth();
  const [governance, setGovernance] = useState(null);
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [gRes, bRes] = await Promise.all([
        api.get('/metrics/governance'),
        api.get('/pms/brief/latest'),
      ]);
      setGovernance(gRes.data);
      setBrief(bRes.data);
    } catch (e) {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDownloadPdf = async () => {
    try {
      const res = await api.get('/pms/report/pdf', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PMS_Report_${brief?.period_start || 'latest'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {}
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="metrics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Shield className="h-6 w-6 text-primary" /> Weekly Brief & Governance</h1>
          <p className="text-muted-foreground">PMS brief (last stored snapshot) and AI config-change history</p>
        </div>
        {brief && !brief.message && user?.export_allowed && (
          <Button variant="outline" size="sm" onClick={handleDownloadPdf} data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-1" /> PDF Report
          </Button>
        )}
      </div>

      <Tabs defaultValue="brief" className="space-y-4">
        <TabsList>
          <TabsTrigger value="brief" data-testid="brief-tab"><FileText className="h-4 w-4 mr-1" /> Weekly Brief</TabsTrigger>
          <TabsTrigger value="governance" data-testid="governance-tab"><Shield className="h-4 w-4 mr-1" /> Governance</TabsTrigger>
        </TabsList>

        {/* Weekly Brief (read-only — PMS is frozen on its last stored snapshot) */}
        <TabsContent value="brief">
          <Card>
            <CardContent className="p-6">
              {!brief || brief.message ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                  <p>No brief on record.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="brief-content">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">{brief.headline}</h2>
                    <Badge className={brief.overall_health === 'STRONG' ? 'bg-green-500' : brief.overall_health === 'STABLE' ? 'bg-blue-500' : brief.overall_health === 'DECLINING' ? 'bg-yellow-500' : 'bg-red-500'}>
                      {brief.overall_health}
                    </Badge>
                  </div>

                  {brief.top_risks?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Top Risks</h3>
                      <div className="space-y-2">
                        {brief.top_risks.map((r, i) => (
                          <div key={i} className={`p-3 rounded-lg border ${r.severity === 'HIGH' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={r.severity === 'HIGH' ? 'bg-red-500 text-white' : 'bg-yellow-500 text-white'}>{r.severity}</Badge>
                              <span className="text-sm font-medium">{r.risk}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Action: {r.action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {brief.best_performers?.length > 0 && (
                    <div className="p-4 border border-green-200 bg-green-50 rounded-lg">
                      <h3 className="text-sm font-semibold text-green-700 mb-2">Best Performers</h3>
                      {brief.best_performers.map((p, i) => (
                        <p key={i} className="text-sm"><span className="font-medium">{p.name}:</span> {p.highlight}</p>
                      ))}
                    </div>
                  )}

                  {brief.recommended_actions?.length > 0 && (
                    <div className="p-4 bg-muted rounded-lg">
                      <h3 className="text-sm font-semibold mb-2">Recommended Actions</h3>
                      <ol className="text-sm list-decimal pl-4 space-y-1">
                        {brief.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
                      </ol>
                    </div>
                  )}

                  {brief.market_signals?.length > 0 && (
                    <div className="p-4 border rounded-lg">
                      <h3 className="text-sm font-semibold mb-2">Market Signals</h3>
                      <ul className="text-sm list-disc pl-4 space-y-1">
                        {brief.market_signals.map((s, i) => <li key={i}>{s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Governance — config-change history only */}
        <TabsContent value="governance">
          <Card>
            <CardHeader><CardTitle className="text-base">Recent AI Config Changes</CardTitle></CardHeader>
            <CardContent>
              {governance?.config_changes?.length > 0 ? (
                <div className="rounded-md border">
                  <Table>
                    <TableHeader><TableRow>
                      <TableHead>Time</TableHead><TableHead>Action</TableHead><TableHead>By</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {governance.config_changes.map((c, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-xs">{formatDateTime(c.timestamp)}</TableCell>
                          <TableCell className="text-sm">{c.action}</TableCell>
                          <TableCell className="text-sm">{c.user_email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No config changes this month</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
